import { Suspense, useEffect, useLayoutEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from './appbar/Appbar';
import PortalSidebar from './sidebar/PortalSidebar';
import { Loader } from '../../components';
import useFetchUser from '../../queries/user/useFetchUser';
import EntityManager from '../../context/EntityManagerLayout';
import useSubscribeStorage from '../../hooks/useSubscribeStorage';
import { PORTAL_ROUTE } from '../../constants/routes';
import useSearchParams from '../../hooks/useSearchParams';
import useQueryData from '../../queries/useQueryData';
import { CUSTOMER_QUERY_KEY, USER_QUERY_KEY } from '../../constants/query';
import { activeCustomerEnvIdSelector, userRoleSelector } from '../../selectors/user';
import CustomerService from '../../services/customer.service';
import { pagesText } from '../../constants/pagesText';
import useFetchCustomers from '../../queries/customer/useFetchCustomers';
import CustomerIdService from '../../services/customer-id.service';
import { Roles } from '../../constants/role';
import { companyNameSelector, customerIdSelector } from '../../selectors/customer';
import useUpdateCustomer from '../../queries/customer/useUpdateCustomer';
import useFetchCustomer from '../../queries/customer/useFetchCustomer';
import EnvironmentIdService from '../../services/environment-id.service';
import { useSetRecoilState } from 'recoil';
import { customerState } from 'src/state/main';

export const APP_BAR_HEIGHT = 64;
export const DRAWER_WIDTH = 280;

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
});

const MainStyle = styled('div')({
  flexGrow: 1,
  minHeight: '100%',
  marginTop: APP_BAR_HEIGHT,
  padding: '48px',
});

const SidebarRoutStyle = styled('div')({
  flexShrink: 0,
  width: DRAWER_WIDTH,
});

export default function PortalLayout() {
  const location = useLocation();
  const setCustomerState = useSetRecoilState(customerState);
  const { isLoading } = useFetchUser();
  const customerQueryId = useQueryData(CUSTOMER_QUERY_KEY, customerIdSelector);
  const companyName = useQueryData(CUSTOMER_QUERY_KEY, companyNameSelector);
  const customerStorageId = CustomerIdService.getId();
  const customerId = customerQueryId || customerStorageId;
  const userRole = useQueryData(USER_QUERY_KEY, userRoleSelector);
  const { isLoading: isLoadingCustomers, data: customerData } = useFetchCustomer({
    id: customerId || 'default',
  });
  const { enqueueSnackbar } = useSnackbar();
  const { data: customersData } = useFetchCustomers();

  const customers = useMemo(() => {
    if (!customersData?.pages?.length) return [];

    return customersData?.pages[0]?.customers;
  }, [customersData?.pages]);

  const navigate = useNavigate();
  const updateCustomerMutation = useUpdateCustomer();
  const { email: queryEmail, customerId: queryCustomerId } = useSearchParams();
  const refreshToken = useSubscribeStorage('refreshToken');
  const customerDefaultEnvId = useQueryData(USER_QUERY_KEY, activeCustomerEnvIdSelector);

  useEffect(() => {
    if (
      !customers?.length &&
      userRole === Roles.superAdmin &&
      !customerId &&
      !customerData?.id &&
      !isLoadingCustomers
    ) {
      navigate(`${PORTAL_ROUTE}/companies`);
      return;
    }

    if ((customerId || customerData?.id) && location?.state?.from === '/login') {
      navigate(`${PORTAL_ROUTE}/apps`, { state: location?.state?.from });
      return;
    }

    if (customers?.length && customers?.length === 1 && !customerId) {
      const firstCustomerId = customers[0].id;
      CustomerIdService.setId(firstCustomerId);
      updateCustomerMutation.mutate({ id: firstCustomerId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customers,
    customerId,
    location?.state?.from,
    userRole,
    customerData?.id,
    navigate,
    updateCustomerMutation,
    isLoadingCustomers,
  ]);

  useEffect(() => {
    if (queryCustomerId === null || queryCustomerId === undefined) return;

    if (queryCustomerId && !queryEmail) {
      navigate(`${PORTAL_ROUTE}/company/environments`);
      return;
    }

    if (!queryEmail) {
      navigate(`${PORTAL_ROUTE}/company/edit`);
      return;
    }

    CustomerService.acceptInvitation({ customerId: queryCustomerId })
      .then(() => {
        navigate(`${PORTAL_ROUTE}/company/edit`);
      })
      .catch(() => {
        enqueueSnackbar(pagesText.failedToAcceptInvitation, {
          variant: 'error',
          autoHideDuration: 3000,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryEmail, queryCustomerId, enqueueSnackbar]);

  useLayoutEffect(() => {
    const defaultEnvironmentId = EnvironmentIdService.getId();
    if (defaultEnvironmentId !== '' && customerDefaultEnvId && companyName) {
      EnvironmentIdService.setId(customerDefaultEnvId);
    }
  }, [customerDefaultEnvId, companyName]);

  useEffect(() => {
    const newState = {};

    if (customerId) {
      newState.customerId = customerId;
    }

    if (customerData?.customers) {
      newState.customers = customerData.customers;
    }

    if (companyName) {
      newState.companyName = companyName;
    }

    if (Object.keys(newState).length > 0) {
      setCustomerState(prev => ({ ...prev, ...newState }));
    }
  }, [companyName, customerData?.customers, customerId, setCustomerState]);

  if (location?.state?.from === '/login' && customerData?.id) {
    if (!customerDefaultEnvId) {
      navigate(`${PORTAL_ROUTE}/company/environments`);
      return;
    }

    return <Navigate to={`${PORTAL_ROUTE}/apps`} state={{ from: location?.state?.from }} />;
  }

  if (
    !isLoadingCustomers &&
    location?.state?.from === '/login' &&
    customers?.length > 1 &&
    !customerData?.id &&
    !customerId &&
    location.pathname !== `${PORTAL_ROUTE}/companies`
  ) {
    return <Navigate to={`${PORTAL_ROUTE}/companies`} />;
  }

  if (!refreshToken) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  return (
    <EntityManager>
      {isLoading ? (
        <Loader />
      ) : (
        <RootStyle>
          <SidebarRoutStyle>
            <CssBaseline />
            <AppBar />
            <PortalSidebar />
          </SidebarRoutStyle>
          <MainStyle>
            <Suspense fallback={<Loader />}>
              <Outlet />
            </Suspense>
          </MainStyle>
        </RootStyle>
      )}
    </EntityManager>
  );
}
